import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ProIcon } from '../components/ProIcon';
import { COMPANY_INFO, buildWhatsAppUrl } from '../config/company';
import { trackContactClick } from '../lib/analytics';
import { ObfuscatedEmail } from '../components/ObfuscatedEmail';

export const About = () => {
  const stats = [
    { icon: 'users', value: '500+', label: 'Guest Stays Hosted' },
    { icon: 'star', value: '4.9/5', label: 'Average Guest Rating' },
    { icon: 'shield', value: '100%', label: 'Homes Personally Inspected' },
    { icon: 'clock', value: '<2hrs', label: 'Average First Response' },
  ];

  const values = [
    {
      icon: 'map-location-dot',
      title: 'We Know the Details',
      description: 'Because we are on the ground in Marbella, we can tell guests what photos cannot: how an area feels, who a home suits, and which details make the stay easier.',
    },
    {
      icon: 'shield',
      title: 'We Protect the Standard',
      description: 'Every home is personally checked before it joins our collection. That protects guests from surprises and gives owners confidence that their property is being represented properly.',
    },
    {
      icon: 'handshake',
      title: 'We Stay Close',
      description: 'From first enquiry to check-out, you deal with a local team that answers quickly, takes ownership, and stays available when plans change.',
    },
  ];

  const differentiators = [
    'Homes checked in person, not approved from photos alone',
    'Straight advice on area, layout, amenities, and family fit',
    'Direct booking with the team that knows the property',
    'Support before arrival, during the stay, and when plans shift',
    'Clear direct rates without platform padding',
    'A selective standard that guests feel and owners value',
  ];

  return (
    <div className="min-h-screen bg-white pt-24 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16 md:mb-24"
        >
          <p className="ui-eyebrow-brand mb-4">About SUNMARBELLA</p>
          <h1 className="ui-page-title mb-6 max-w-4xl mx-auto">
            Marbella Rentals Done Properly.
          </h1>
          <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            Guests come to us for homes that feel as good as they look online. Owners choose us for the same reason:
            we protect standards, communicate clearly, and stay close to every stay.
          </p>
        </motion.div>

        {/* Stats Grid */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="mb-16 md:mb-24"
        >
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-6 text-center border border-slate-100 shadow-sm hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center mx-auto mb-3 text-brand-ocean">
                  <ProIcon icon={stat.icon} family="duotone" className="w-6 h-6" />
                </div>
                <div className="text-2xl sm:text-3xl font-black text-brand-ocean mb-1">{stat.value}</div>
                <div className="text-sm font-semibold text-slate-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Story Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 mb-16 md:mb-24">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="ui-section-title mb-6">Built Around Trust</h2>
            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                SUNMARBELLA was built around a simple idea: booking a holiday home should feel reassuring, not risky.
                Too many guests were arriving at homes that were over-promised online, underwhelming in reality,
                or unsupported the moment something went wrong.
              </p>
              <p>
                We are a Marbella-based team, so we can guide you beyond the listing. We know which areas suit families,
                where you can genuinely walk to dinner, which homes deliver privacy, and which details make the biggest
                difference once you arrive.
              </p>
              <p>
                That is why we keep our collection selective. Every home is personally checked before it goes live,
                and we stay involved before, during, and after the booking. Guests get peace of mind. Owners get a
                partner who takes their home seriously.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="bg-white rounded-3xl p-8 border border-slate-100 shadow-sm"
          >
            <h3 className="text-xl font-bold text-brand-ocean mb-6">What That Looks Like in Practice</h3>
            <ul className="space-y-4">
              {differentiators.map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  className="flex items-start gap-3"
                >
                  <div className="w-6 h-6 bg-brand-ocean/10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                    <ProIcon icon="check" family="solid" className="w-4 h-4 text-brand-ocean" />
                  </div>
                  <span className="text-slate-700">{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>

        {/* Values Section */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16 md:mb-24"
        >
          <div className="text-center mb-12">
            <h2 className="ui-section-title">How We Work</h2>
            <p className="text-slate-600 mt-4 max-w-2xl mx-auto">
              Three principles shape how we choose homes, support guests, and represent owners.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm hover:shadow-md transition-all"
              >
                <div className="w-12 h-12 bg-brand-ocean/10 rounded-xl flex items-center justify-center mb-5 text-brand-ocean">
                  <ProIcon icon={value.icon} family="duotone" className="text-xl" />
                </div>
                <h3 className="text-xl font-bold text-brand-ocean mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* For Property Owners */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="mb-16 md:mb-24"
        >
          <div className="bg-brand-sand rounded-3xl p-8 md:p-12 border border-slate-100">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="ui-eyebrow-muted mb-4">For Property Owners</p>
                <h2 className="ui-section-title mb-4">Own a Property in Marbella?</h2>
                <p className="text-slate-600 leading-relaxed mb-6">
                  We also work with a select group of owners who want more than occupancy. If you care about stronger
                  presentation, better guest fit, and a local team that protects the standard of your home, we should talk.
                </p>
                <Link
                  to="/owners"
                  onClick={() => trackContactClick('about_cta', 'homeowner_partnership')}
                  className="ui-btn-secondary gap-2 group"
                >
                  <span>Partner With Us</span>
                  <ProIcon icon="chevron-right" family="light" className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                </Link>
              </div>
              <div className="hidden md:grid grid-cols-2 gap-4">
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <ProIcon icon="house-building" family="light" className="w-8 h-8 text-brand-ocean mb-2" />
                  <p className="font-bold text-slate-900">Stronger Presentation</p>
                </div>
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <ProIcon icon="family-dress" family="solid" className="w-8 h-8 text-brand-ocean mb-2" />
                  <p className="font-bold text-slate-900">Better Guest Fit</p>
                </div>
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <ProIcon icon="handshake" family="light" className="w-8 h-8 text-brand-ocean mb-2" />
                  <p className="font-bold text-slate-900">Local Oversight</p>
                </div>
                <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
                  <ProIcon icon="star" family="light" className="w-8 h-8 text-brand-ocean mb-2" />
                  <p className="font-bold text-slate-900">Premium Positioning</p>
                </div>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Contact CTA */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="bg-gradient-to-br from-brand-ocean to-brand-ocean-dark rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden"
        >
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white rounded-full translate-y-1/2 -translate-x-1/2"></div>
          </div>

          <div className="relative z-10 max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-black mb-4 leading-tight">
              Start With a Real Conversation.
            </h2>
            <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              Whether you are planning a holiday or asking about your property, you will speak to a real Marbella team
              that answers clearly and quickly.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {/* Desktop Contact Link */}
              <Link
                to="/contact"
                onClick={() => trackContactClick('about_cta', 'contact_us')}
                className="hidden sm:inline-flex ui-btn-secondary gap-2 group"
              >
                <ProIcon icon="envelope-open" family="light" className="w-5 h-5" />
                <span>Talk to Our Team</span>
                <ProIcon icon="chevron-right" family="light" className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>

              {/* Mobile Contact Link */}
              <Link
                to="/contact"
                onClick={() => trackContactClick('about_cta', 'contact_us')}
                className="sm:hidden ui-btn-secondary gap-2 group"
              >
                <ProIcon icon="envelope-open" family="light" className="w-5 h-5" />
                <span>Talk to Our Team</span>
              </Link>

              {/* WhatsApp Button */}
              <a
                href={buildWhatsAppUrl()}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackContactClick('about_cta', 'chat_on_whatsapp')}
                className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#128C7E] text-white font-black px-8 py-4 rounded-2xl transition-all duration-200 active:scale-[0.98]"
                aria-label="Chat on WhatsApp"
              >
                <ProIcon icon="whatsapp" family="brands" className="w-5 h-5" />
                <span>Chat on WhatsApp</span>
              </a>
            </div>

            {/* Contact Info */}
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 text-sm text-white/70">
              <a href={COMPANY_INFO.phoneHref} className="flex items-center gap-2 hover:text-white transition-colors">
                <ProIcon icon="phone-connection" family="light" className="w-4 h-4" />
                <span>{COMPANY_INFO.phone}</span>
              </a>
              <span className="hidden sm:inline">•</span>
              <div className="flex items-center gap-2 hover:text-white transition-colors">
                <ProIcon icon="envelope-open" family="light" className="w-4 h-4" />
                <ObfuscatedEmail className="hover:text-white transition-colors" />
              </div>
            </div>
          </div>
        </motion.section>
      </div>
    </div>
  );
};
